import React, { useRef, useState } from "react";
import { Save, X } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import DatePicker from "../../../../components/DatePicker";
import { parseEgyptianNationalId, normalizeNationalId } from "../../../../utils/egyptianNationalId";
import { API_URL } from "../../config";

// ===== RESPONSIVE EDIT EMPLOYEE MODAL COMPONENT =====
const EditEmployeeModal = ({
  styles,
  text,
  language,
  theme,
  screenSize,
  loading,
  selectedEmployee,
  onUpdate,
  onCancel,
  onBackdropClick,
}) => {
  // Initialize form data with selected employee data
  const [formData, setFormData] = useState({
    name: selectedEmployee?.name || "",
    email: selectedEmployee?.email || "",
    phone: selectedEmployee?.phone || "",
    department: selectedEmployee?.department || "",
    laborMode: selectedEmployee?.laborMode || "",
    lastContractTerm:
      selectedEmployee?.lastContractTerm ||
      selectedEmployee?.fullEmployeeData?.lastContractTerm ||
      "",
    company: selectedEmployee?.company || "",
    status: selectedEmployee?.status || "Active",
    salary: selectedEmployee?.salary || "",
    grossSalary: selectedEmployee?.grossSalary || selectedEmployee?.fullEmployeeData?.grossSalary || "",
    salaryInputSource: "",
    insuranceSalary: selectedEmployee?.fullEmployeeData?.insuranceSalary || "",
    nationalId: selectedEmployee?.fullEmployeeData?.nationalId || "",
    address: selectedEmployee?.fullEmployeeData?.address || "",
    city: selectedEmployee?.fullEmployeeData?.city || "",
    country: selectedEmployee?.fullEmployeeData?.country || "Egypt",
    nationality: selectedEmployee?.fullEmployeeData?.nationality || "",
    gender: selectedEmployee?.fullEmployeeData?.gender || "",
    maritalStatus: selectedEmployee?.fullEmployeeData?.maritalStatus || "",
    dateOfBirth: selectedEmployee?.fullEmployeeData?.dateOfBirth
      ? new Date(selectedEmployee.fullEmployeeData.dateOfBirth)
        .toISOString()
        .split("T")[0]
      : "",
  });

  // Form validation errors state
  const [formErrors, setFormErrors] = useState({});

  // Editing the national ID to a full valid Egyptian ID refills date of
  // birth and gender from it, plus nationality only when empty. Opening the
  // modal never overwrites stored values — only an actual change to the ID.
  const nationalIdInvalid =
    normalizeNationalId(formData.nationalId).length >= 14 &&
    !parseEgyptianNationalId(formData.nationalId);
  const handleNationalIdChange = (e) => {
    const value = e.target.value;
    const info = parseEgyptianNationalId(value);
    setFormData((prev) => ({
      ...prev,
      nationalId: value,
      ...(info && {
        dateOfBirth: info.dateOfBirth,
        gender: info.gender,
        nationality: prev.nationality || (language === "ar" ? "مصري" : "Egyptian"),
      }),
    }));
  };

  // Handle input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear error when user starts typing
    if (formErrors[e.target.name]) {
      setFormErrors({
        ...formErrors,
        [e.target.name]: "",
      });
    }
  };

  // Gross/Net salary are mutually derived: whichever one the user types into
  // becomes `salaryInputSource` (sent to the backend as the authoritative
  // figure on save); the other field locks and shows a live preview fetched
  // from the server — same formulas the save itself will use, never
  // duplicated in the frontend.
  const salaryPreviewTimer = useRef(null);
  const handleSalaryFieldChange = (name, value) => {
    const source = name === "grossSalary" ? "gross" : "net";
    setFormData((prev) => ({ ...prev, [name]: value, salaryInputSource: source }));

    if (salaryPreviewTimer.current) clearTimeout(salaryPreviewTimer.current);
    if (value === "" || value === null || isNaN(Number(value))) return;

    salaryPreviewTimer.current = setTimeout(async () => {
      try {
        const response = await apiFetch(`${API_URL}/employees/salary-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grossSalary: source === "gross" ? value : null,
            netSalary: source === "net" ? value : null,
            salaryInputSource: source,
            insuranceSalary: formData.insuranceSalary || null,
          }),
        });
        const data = await response.json();
        if (data?.success) {
          setFormData((prev) => ({
            ...prev,
            grossSalary: source === "gross" ? prev.grossSalary : data.grossSalary ?? prev.grossSalary,
            salary: source === "net" ? prev.salary : data.netSalary ?? prev.salary,
          }));
        }
      } catch (err) {
        console.error("Salary preview failed:", err);
      }
    }, 400);
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    // Check if name is provided
    if (!formData.name.trim()) {
      errors.name = text[language].nameRequired;
    }

    // Check if email is provided and valid
    if (!formData.email.trim()) {
      errors.email = text[language].emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = text[language].emailInvalid;
    }

    // Check if department is provided
    if (!formData.department.trim()) {
      errors.department = text[language].departmentRequired;
    }

    // Update errors state
    setFormErrors(errors);
    // Return true if no errors
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only proceed if form is valid
    if (!validateForm()) {
      return;
    }

    // Call update function
    await onUpdate(formData);
  };

  // Return modal JSX
  return (
    <div
      style={styles.modal}
      onClick={onBackdropClick}
    >
      <div
        style={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{text[language].editEmployee}</h2>
          <button
            style={styles.closeBtn}
            onClick={onCancel}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].basicInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].fullName} *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.name
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterFullName}
              />
              {formErrors.name && (
                <span style={styles.errorText}>{formErrors.name}</span>
              )}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].emailAddress} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={{
                  ...styles.formInput,
                  borderColor: formErrors.email
                    ? "#ef4444"
                    : theme === "dark"
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
                placeholder={text[language].enterEmail}
              />
              {formErrors.email && (
                <span style={styles.errorText}>{formErrors.email}</span>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].phoneNumber}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterPhone}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].nationalId}
              </label>
              <input
                type="text"
                inputMode="numeric"
                name="nationalId"
                value={formData.nationalId}
                onChange={handleNationalIdChange}
                style={{
                  ...styles.formInput,
                  ...(nationalIdInvalid && { borderColor: "#ef4444" }),
                }}
                placeholder={text[language].enterNationalId}
              />
              {nationalIdInvalid && (
                <p style={{ fontSize: 12, margin: "6px 0 0", color: "#ef4444" }}>
                  {language === "ar"
                    ? "الرقم القومي غير صحيح — لم يتم ملء البيانات تلقائيًا."
                    : "Invalid national ID — details were not auto-filled."}
                </p>
              )}
            </div>
          </div>

          {/* Work Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].workInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].department} *
              </label>
              <ReusableDropdown
                name="department"
                items={[
                  { id: "Information Technology", label: text[language].infotech },
                ]}
                value={formData.department}
                onChange={(value) => handleInputChange({ target: { name: "department", value } })}
                placeholder={text[language].selectDepartment}
                inputStyle={formErrors.department ? { borderColor: "#ef4444" } : undefined}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
              {formErrors.department && (
                <span style={styles.errorText}>{formErrors.department}</span>
              )}
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].laborMode}
              </label>
              <ReusableDropdown
                name="laborMode"
                items={[
                  { id: "Insource", label: text[language].insource },
                  { id: "Outsource", label: text[language].outsource },
                ]}
                value={formData.laborMode}
                onChange={(value) => handleInputChange({ target: { name: "laborMode", value } })}
                placeholder={text[language].selectLaborMode}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].lastContractTerm}
              </label>
              <ReusableDropdown
                name="lastContractTerm"
                items={[
                  { id: "Full-time", label: text[language].fullTime },
                  { id: "Part-time", label: text[language].partTime },
                  { id: "Contract", label: text[language].contract },
                  { id: "Temporary", label: text[language].temporary },
                  { id: "Freelance", label: text[language].freelance },
                ]}
                value={formData.lastContractTerm}
                onChange={(value) => handleInputChange({ target: { name: "lastContractTerm", value } })}
                placeholder={text[language].selectLastContractTerm}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].companyName}
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterCompany}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].statusLabel}
              </label>
              <ReusableDropdown
                name="status"
                items={[
                  { id: "Active", label: text[language].statusActive },
                  { id: "OnLeave", label: text[language].statusOnLeave },
                  { id: "Inactive", label: text[language].statusInactive },
                ]}
                value={formData.status}
                onChange={(value) => handleInputChange({ target: { name: "status", value } })}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {language === "ar" ? "الراتب الإجمالي" : "Gross Salary"}
              </label>
              <input
                type="number"
                name="grossSalary"
                value={formData.grossSalary}
                onChange={(e) => handleSalaryFieldChange("grossSalary", e.target.value)}
                style={styles.formInput}
                placeholder={language === "ar" ? "أدخل الراتب الإجمالي" : "Enter gross salary"}
                min="0"
                step="any"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].salaryLabel}
              </label>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={(e) => handleSalaryFieldChange("salary", e.target.value)}
                style={styles.formInput}
                placeholder={text[language].enterSalary}
                min="0"
                step="any"
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].gender}</label>
              <ReusableDropdown
                name="gender"
                items={[
                  { id: "Male", label: text[language].male },
                  { id: "Female", label: text[language].female },
                ]}
                value={formData.gender}
                onChange={(value) => handleInputChange({ target: { name: "gender", value } })}
                placeholder={text[language].selectGender}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>
          </div>

          {/* Personal Information Section */}
          <div style={styles.sectionTitle}>
            {text[language].personalInformation}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].dateOfBirth}
              </label>
              <DatePicker
                theme={theme}
                isRTL={language === "ar"}
                value={formData.dateOfBirth || ""}
                onChange={(v) => handleInputChange({ target: { name: "dateOfBirth", value: v } })}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].maritalStatus}
              </label>
              <ReusableDropdown
                name="maritalStatus"
                items={[
                  { id: "Single", label: text[language].single },
                  { id: "Married", label: text[language].married },
                  { id: "Divorced", label: text[language].divorced },
                  { id: "Widowed", label: text[language].widowed },
                ]}
                value={formData.maritalStatus}
                onChange={(value) => handleInputChange({ target: { name: "maritalStatus", value } })}
                placeholder={text[language].selectMaritalStatus}
                getItemKey={(item) => item.id}
                getItemLabel={(item) => item.label}
                searchable={false}
                theme={theme}
                language={language}
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {text[language].nationality}
              </label>
              <input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterNationality}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{text[language].city}</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                style={styles.formInput}
                placeholder={text[language].enterCity}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{text[language].address}</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={styles.formInput}
              placeholder={text[language].enterAddress}
            />
          </div>

          {/* Modal Actions */}
          <div style={styles.modalActions}>
            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={onCancel}
            >
              {text[language].cancel}
            </button>
            <button
              type="submit"
              style={{
                ...styles.primaryBtn,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
            >
              <Save size={screenSize === "xs" ? 14 : 16} />
              {loading ? text[language].updating : text[language].update}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployeeModal;
